import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { formatCustomerName } from "../../utils/utils.js";
import "./checklistForm.css";

const ChecklistForm = ({
  template,
  existingChecklist,
  customer,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const sigCanvas = useRef(null);
  const signatureBoxRef = useRef(null);
  const [acknowledgedItems, setAcknowledgedItems] = useState({});
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signerName, setSignerName] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const checklistItems = useMemo(() => {
    return existingChecklist?.items?.length
      ? existingChecklist.items
      : template?.items || [];
  }, [existingChecklist, template]);

  const hasExistingSignature = Boolean(
    existingChecklist?.signature?.imageDataUrl ||
    existingChecklist?.signature?.storagePath ||
    existingChecklist?.signature?.signedByName
  );

  useEffect(() => {
    const initialAcknowledgements = {};
    checklistItems.forEach((item) => {
      initialAcknowledgements[item.id] = item.acknowledged === true;
    });

    setAcknowledgedItems(initialAcknowledgements);
    setSignerName(
      existingChecklist?.signature?.signedByName ||
      existingChecklist?.signer?.name ||
      formatCustomerName(customer, "")
    );
    setSignatureDataUrl(existingChecklist?.signature?.imageDataUrl || "");
    setSubmitAttempted(false);

    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  }, [checklistItems, existingChecklist, customer]);

  const requiredItemsAcknowledged = checklistItems.every((item) => {
    return item.required === false || acknowledgedItems[item.id] === true;
  });

  const signatureReady = Boolean(signatureDataUrl || hasExistingSignature);
  const canSubmit = Boolean(requiredItemsAcknowledged && signatureReady && signerName.trim() && !isSubmitting);

  const resizeSignatureCanvas = useCallback(() => {
    const signaturePad = sigCanvas.current;
    const canvas = signaturePad?.getCanvas?.();
    const signatureBox = signatureBoxRef.current;

    if (!signaturePad || !canvas || !signatureBox) return;

    const { width, height } = signatureBox.getBoundingClientRect();
    if (!width || !height) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvasWidth = Math.round(width * ratio);
    const canvasHeight = Math.round(height * ratio);

    if (canvas.width === canvasWidth && canvas.height === canvasHeight) return;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.getContext("2d").scale(ratio, ratio);
    signaturePad.clear();
    setSignatureDataUrl(existingChecklist?.signature?.imageDataUrl || "");
  }, [existingChecklist]);

  useLayoutEffect(() => {
    const signatureBox = signatureBoxRef.current;
    if (!signatureBox) return undefined;

    let animationFrameId;
    const resize = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(resizeSignatureCanvas);
    };

    resize();
    window.addEventListener("resize", resize);

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(signatureBox);
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resize);
      resizeObserver?.disconnect();
    };
  }, [resizeSignatureCanvas]);

  const handleAcknowledge = (itemId) => {
    setAcknowledgedItems((current) => ({
      ...current,
      [itemId]: !current[itemId],
    }));
  };

  const captureSignature = () => {
    if (sigCanvas.current?.isEmpty()) {
      setSignatureDataUrl("");
      return;
    }

    const dataUrl = sigCanvas.current.getTrimmedCanvas().toDataURL("image/png");
    setSignatureDataUrl(dataUrl);
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setSignatureDataUrl("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitAttempted(true);

    if (!canSubmit) return;

    const now = new Date().toISOString();
    onSubmit({
      templateId: template.id,
      status: "completed",
      signer: {
        name: signerName.trim(),
        role: "renter",
        email: customer?.email || "",
      },
      items: checklistItems.map((item) => ({
        id: item.id,
        acknowledged: acknowledgedItems[item.id] === true,
        acknowledgedAt: acknowledgedItems[item.id] === true
          ? item.acknowledgedAt || now
          : null,
      })),
      signature: {
        signedByName: signerName.trim(),
        signedAt: now,
        imageDataUrl: signatureDataUrl || existingChecklist?.signature?.imageDataUrl || null,
        storagePath: existingChecklist?.signature?.storagePath || null,
      },
      file: existingChecklist?.file || null,
    });
  };

  if (!template) {
    return (
      <div className="checklist-empty">
        No active checklist template was found.
      </div>
    );
  }

  return (
    <form className="checklist-form" onSubmit={handleSubmit}>
      <div className="checklist-template-header">
        <div>
          <h3>{template.name}</h3>
          {template.description && <p>{template.description}</p>}
        </div>
        <div className="checklist-template-meta">
          Version {template.version || 1}
        </div>
      </div>

      {template.business && (
        <div className="checklist-business">
          <strong>{template.business.name}</strong>
          <span>
            {template.business.addressLine1}, {template.business.city}, {template.business.state} {template.business.postalCode}
          </span>
          <span>{template.business.phone}</span>
        </div>
      )}

      {existingChecklist?.completedAt && (
        <div className="checklist-completed-banner">
          Completed {new Date(existingChecklist.completedAt).toLocaleString()}
        </div>
      )}

      <div className="checklist-items">
        {checklistItems.map((item, index) => {
          const acknowledged = acknowledgedItems[item.id] === true;

          return (
            <button
              type="button"
              key={item.id}
              className={`checklist-item ${acknowledged ? "acknowledged" : ""}`}
              onClick={() => handleAcknowledge(item.id)}
            >
              <span className="checklist-item-number">{index + 1}</span>
              <span className="checklist-item-text">
                {item.label}
                {item.description && <small>{item.description}</small>}
              </span>
              <span className="checklist-item-status">
                {acknowledged ? "Acknowledged" : "Tap to acknowledge"}
              </span>
            </button>
          );
        })}
      </div>

      {submitAttempted && !requiredItemsAcknowledged && (
        <div className="checklist-error">
          All required checklist items must be acknowledged.
        </div>
      )}

      <div className="checklist-signature-section">
        <label htmlFor="checklistSignerName">Signer Name</label>
        <input
          id="checklistSignerName"
          className="form-control"
          value={signerName}
          onChange={(event) => setSignerName(event.target.value)}
          placeholder="Customer name"
        />

        <div className="checklist-signature-box" ref={signatureBoxRef}>
          <SignatureCanvas
            ref={sigCanvas}
            onEnd={captureSignature}
            canvasProps={{
              className: "checklist-signature-canvas",
            }}
          />
        </div>

        {hasExistingSignature && !signatureDataUrl && (
          <div className="checklist-signature-note">
            A signature has already been captured for this checklist. Sign again only if you need to replace it.
          </div>
        )}

        {submitAttempted && !signatureReady && (
          <div className="checklist-error">
            A customer signature is required.
          </div>
        )}
      </div>

      <div className="modal-btn-container checklist-actions">
        <button type="button" className="form-secondary-btn btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn" onClick={clearSignature}>
          Clear Signature
        </button>
        <button type="submit" className="form-primary-btn btn" disabled={!canSubmit}>
          {isSubmitting ? "Saving..." : "Save Checklist"}
        </button>
      </div>
    </form>
  );
};

export default ChecklistForm;
